/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { State } from "store/reducers";
import ls from "Localization";
import _ from "lodash";
import { Helmet } from "react-helmet-async";

import actions from "store/actions";

import dayjs from "dayjs";
import Report from "Components/Report";

import ImportModal from "./Import";
import ReactTable from "react-table";
import IMei from "Types/IMei";
import { MeiState } from "store/reducers/mei";
import { CNPJ, CPF } from "Components/NumberFormat";
import { useNavigate } from "react-router-dom";
import DateSelectFilter from "Components/Report/DateSelectorFilter";
import { dateSort } from "Components/Report/helpers";
import { Tooltip } from "@mui/material";
import { MeiSituation } from "Types/IMei";

let getMeisDebounced: (() => any) & _.Cancelable;

function Meis() {
	const navigate = useNavigate();
	const dispatch = useDispatch<any>();
	const table = useRef<ReactTable<any>>(null);

	const state = useSelector<State, MeiState>(s => s.mei);

	const { user } = useSelector<State, any>(s => s.login);

	const handleFetchData = (tableState: any) => {
		if (!tableState) return;
		const { page, pageSize, toExport, callback } = tableState;
		let { sorted, filtered } = tableState;

		if (getMeisDebounced) {
			getMeisDebounced.cancel();
		}

		if (!sorted) sorted = [];

		const creationDate = filtered.find((c: any) => c.id === "createdAt");

		if (creationDate?.value) {
			filtered = filtered.filter((c: any) => c.id !== "createdAt");

			if (creationDate?.value.startDate) {
				filtered.push({
					id: "startDate",
					value: creationDate.value.startDate
				});
			}

			if (creationDate?.value.endDate)
				filtered.push({
					id: "endDate",
					value: creationDate.value.endDate
				});
		}

		getMeisDebounced = _.debounce(
			() =>
				dispatch(
					actions.mei.getMeis(
						page * pageSize,
						pageSize,
						filtered.reduce(
							(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
							"&filters[type]=0"
						),
						sorted[0]?.id,
						sorted[0]?.desc,
						toExport,
						callback
					)
				),
			500
		);

		getMeisDebounced();
	};

	const load = () => handleFetchData(table.current?.state);

	const columns = useMemo(() => {
		return [
			{
				Header: ls.creationDate,
				id: "createdAt",
				accessor: (c: IMei) =>
					c.createdAt
						? dayjs(c.createdAt).format(ls.dateFormatWithoutSeconds)
						: "",
				show: true,
				sortMethod: dateSort,
				width: 150,
				Filter: p => <DateSelectFilter {...p} />
			},
			{
				Header: ls.name,
				id: "businessName",
				show: true,
				accessor: (c: IMei) => {
					return c.businessName || c.personName || "";
				}
			},
			{
				Header: ls.cnpj,
				id: "cnpj",
				accessor: (c: IMei) => <CNPJ value={c.cnpj} displayType="text" />,
				show: true,
				width: 160
			},
			{
				Header: "CNAE Principal",
				id: "cnaePrincipal",
				show: false,
				accessor: (c: any) => {
					return c.queryData?.atividade?.atividadePrincipal
						?.codigoDescricaoFormatado;
				}
			},
			{
				Header: "CNAEs Secundários",
				id: "cnaeSecundarias",
				show: false,
				accessor: (c: any) => {
					return c.queryData?.atividade?.atividadesSecundarias
						?.map((c: any) => c.codigoDescricaoFormatado)
						.join(", ");
				}
			},
			{
				Header: ls.cpf,
				id: "cpf",
				accessor: (c: IMei) => <CPF value={c.cpf} displayType="text" />,
				show: true,
				width: 130
			},
			{
				Header: ls.openingDate,
				id: "openingDate",
				accessor: (c: IMei) =>
					c.openingDate
						? dayjs(Number(c.openingDate)).format(ls.dateFormatWithoutSeconds)
						: "",
				show: true,
				filterable: false,
				sortMethod: dateSort,
				width: 150,
				Filter: p => <DateSelectFilter {...p} />
			},
			{
				Header: ls.emissionDate,
				id: "emissionDate",
				accessor: (c: IMei) =>
					c.emissionDate
						? dayjs(c.emissionDate, "DD/MM/YYYY").format(
								ls.dateFormatWithoutSeconds
						  )
						: "",
				show: true,
				filterable: false,
				sortMethod: dateSort,
				width: 150,
				Filter: p => <DateSelectFilter {...p} />
			},
			{
				Header: ls.situation,
				id: "situation",
				show: true,
				accessor: (c: IMei) => {
					if (c.situation !== undefined) {
						if (c.situation === "") {
							return (
								<Tooltip title={c.situation}>
									<div>{c.situation}</div>
								</Tooltip>
							);
						}
						return c.situation;
					}
				},
				width: 100,
				Filter: ({ filter, onChange }: any) => {
					return (
						<select
							onChange={event => onChange(event.target.value)}
							style={{ width: "100%" }}
							value={filter ? filter.value : "all"}
						>
							<option value="">Todos</option>
							{Object.keys(MeiSituation).map((c: any) => (
								<option key={c} value={c}>
									{c}
								</option>
							))}
						</select>
					);
				}
			},
			{
				Header: ls.actions,
				id: "actions",
				accessor: () => (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flex: 1
						}}
					>
						{/* <a
							href="!#"
							onClick={e => {
								e.preventDefault();
								handleResendMessage(c._id);
							}}
						>
							<Tooltip title="Enviar:">
								<RedoOutlinedIcon />
							</Tooltip>
						</a> */}
					</div>
				),
				show: true,
				filterable: false,
				width: 80
			}
		];
	}, []);

	return (
		<div>
			<Helmet>
				<title>Prestadores de serviço | {ls.appName}</title>
			</Helmet>
			<Report
				manual
				tableRef={table}
				headerRightComponent={
					user.roles.includes("admin") ? <ImportModal load={load} /> : null
				}
				titleText="Prestadores de serviço"
				title="Prestadores de serviço"
				data={state.items}
				pages={state.pages}
				loading={state.loading.getAll}
				columns={columns}
				onFetchData={handleFetchData}
				filterable
				onRowClicked={data => navigate(`/Dashboard/Mei/${data._id}`)}
				visibleColumns={columns
					.filter(c => c.show)
					.map(c => c.Header.toString())}
				defaultSorted={[
					{
						id: "createdAt",
						desc: true
					}
				]}
				getTdProps={() => {
					return {
						style: {
							display: "flex",
							alignItems: "center"
						}
					};
				}}
			/>
		</div>
	);
}

export default Meis;
